import type { FC } from 'react'
import Select from 'react-select'
import type { GroupBase, Props } from 'react-select'
import { Typography } from '@mui/material'

import { form as formApi, taskTemplates as taskTemplatesApi } from '@fireflyhealth/api'
import { usePromise } from '~/shared/hooks/usePromise'
import { selectCustomStyles } from './selectCustomStyles'

// Derived from the calls below rather than named imports, so the option shapes track
// the generated client instead of drifting from it.
type FormOption = Awaited<ReturnType<typeof formApi.list>>['data'][number]
type TaskOption = Awaited<ReturnType<typeof taskTemplatesApi.list>>['data'][number]
export type FormSelectOption = FormOption | TaskOption
type Option = FormSelectOption
type Group = GroupBase<Option>

// A virtual task carries the collection it belongs to; a plain form does not.
export const isTaskOption = (option: FormSelectOption): option is TaskOption =>
  'collection_id' in option

const FormSelect: FC<Props<Option, false, Group>> = props => {
  const { result } = usePromise(
    async () => {
      // Fetch both virtual task templates and available forms
      const [forms, taskCollections] = await Promise.all([
        formApi.list().then(r => r.data),
        taskTemplatesApi.list({ params: { source_type: 'virtual' } }).then(r => r.data),
      ])
      // If there are any virtual tasks tied to forms, prefer the virtual
      // task template so its metadata can be accessed downstream
      const virtualTaskTitles = taskCollections.map(v => v.title)
      // Sorting
      const sortedForms = [...forms]
        .filter(f => !virtualTaskTitles.includes(f.title))
        .sort((a, b) => a.title.localeCompare(b.title))
      const sortedTaskCollections = [...taskCollections].sort((a, b) =>
        a.title.localeCompare(b.title),
      )
      // Format responses to fit react-select grouped options
      return [
        { label: 'Forms', options: sortedForms },
        { label: 'Virtual Tasks', options: sortedTaskCollections },
      ]
    },
    { error: 'Failed to load form and task template info' },
  )

  return (
    <Select<Option, false, Group>
      options={result}
      getOptionLabel={option => {
        // If it's a collection, prefer uid
        if (isTaskOption(option)) return option.uid ?? option.title
        return option.title
      }}
      getOptionValue={option => String(option.id)}
      formatGroupLabel={group => <Typography>{group.label}</Typography>}
      styles={selectCustomStyles}
      placeholder="Select Form"
      {...props}
    />
  )
}

export default FormSelect
